import React, {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faUser,
    faCalendarAlt,
    faEdit,
    faTrash,
    faUsers,
    faExclamationTriangle,
    faGift,
    faGripVertical,
    faChevronDown,
    faChevronUp,
    faInfoCircle
} from '@fortawesome/free-solid-svg-icons';
import { Expense, ShareRatios, SponsorExpense } from '../types';
import { DEFAULT_SHARE_RATIO, formatDate, formatShareRange, getShareRatio, startOfDay } from '../utils/helpers';
import { useEventUsersCache } from '../hooks/useEventUsersCache';
import { useTranslation } from 'react-i18next';
import { useFormatCurrency } from '../hooks/useFormatCurrency';

type AnyExpense = Expense | SponsorExpense;

interface ExpenseListProps {
    expenses: AnyExpense[];
    eventId: string;
    // Names straight off the event meta. Available as soon as the event loads,
    // unlike the eventUsers node, which arrives on its own listener.
    participants?: { [userId: string]: string };
    // Who carries more or less than a full share: it changes the "each" figure,
    // and the names say so, or a smaller share would read as a mistake.
    shareRatios?: ShareRatios;
    currentUser: string;
    isAdmin: boolean;
    onEdit?: (expense: AnyExpense) => void;
    onDelete?: (expenseId: string) => void;
    onReorder?: (payload: ReorderPayload) => void;
    canEdit?: (expense: AnyExpense) => boolean;
    canDelete?: (expense: AnyExpense) => boolean;
}

export interface ReorderPayload {
    // Every visible entry in its new order, newest first. A drag can travel any
    // distance and across days, which no sequence of neighbour swaps describes.
    order: AnyExpense[];
    movedId: string;
    // Local midnight of the day the moved entry now belongs to. Dropping it among
    // another day's entries is a request to move it to that day, so the caller
    // gets told which one rather than having to infer it.
    targetDayStart: number;
}

/**
 * The day an entry joins by being dropped where it was.
 *
 * It takes the day of the entry it landed under — dropping something below the
 * last line of Tuesday reads as putting it at the end of Tuesday. Only at the very
 * top of the list, where nothing is above it, does the entry below decide instead.
 */
const targetDayFor = (order: AnyExpense[], index: number, moved: AnyExpense): number => {
    const anchor = order[index - 1] ?? order[index + 1];
    return startOfDay(anchor ? anchor.timestamp : moved.timestamp);
};

// How long the handle has to be held before the list collapses and the drag takes
// over. Short enough not to feel like waiting, long enough that brushing the
// handle does nothing.
const HOLD_MS = 200;
// ...or this far of a deliberate drag, whichever comes first.
const MOVE_ACTIVATE_PX = 8;
// How deep past an edge the finger has to be before the page starts travelling
// under it, and how fast it goes once it is all the way in. The speed ramps across
// the zone instead of switching on at full tilt: entering the zone by a pixel used
// to launch the list at ~700px/s, which is most of what made a drag on a phone
// feel like it had got away.
const EDGE_SCROLL_ZONE = 72;
const EDGE_SCROLL_MAX_STEP = 14;
// The sticky header covers the top of the viewport, so the top zone starts below
// it rather than at the screen edge — otherwise the zone is somewhere the row
// being dragged cannot be seen anyway.
const HEADER_INSET_PX = 64;
// The gap between two collapsed lines. Named rather than a utility class because
// the overlays are placed by arithmetic off it, and a class they could not read
// would drift away from them silently.
const COLLAPSED_GAP_PX = 10;
// How long the entry that was just moved keeps its mark. Long enough to catch the
// eye once the list has sprung back to full-height cards, short enough to be gone
// before it starts reading as a selection.
const LANDED_HIGHLIGHT_MS = 1800;
// Frames to let the reordered list arrive before scrolling to the moved card: the
// new order comes back through Firebase's local write, a render after the finger
// lifts, and scrolling any sooner aims at where the card used to be.
const LANDED_SETTLE_FRAMES = 2;
// The breathing room the moved card is given from the bottom edge, and from the
// sticky header at the top (HEADER_INSET_PX).
const LANDED_VIEW_MARGIN_PX = 16;

interface PressState {
    id: string;
    order: AnyExpense[];
    startY: number;
    activated: boolean;
}

// The entry a move has just left somewhere the eye has to be led back to.
// `focusHandle` is set only for the keyboard path, where the handle carrying the
// focus can be remounted out from under the key that is still being pressed.
interface LandedState {
    id: string;
    focusHandle: boolean;
}

interface DragState {
    id: string;
    fromIndex: number;
    currentIndex: number;
    // How far the finger has travelled since the drag began. Drives the index.
    offsetY: number;
    // A fixed visual nudge that puts the row under the finger after the collapse
    // reflowed the page. Deliberately kept out of the index maths: folding it in
    // would count the reflow as if the user had dragged that far.
    liftCorrection: number;
    pitch: number;
    // Pitch minus this is the gap between two lines, which is where the day rules
    // and the drop caret are drawn.
    rowHeight: number;
}

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

// An explicit `behavior: 'smooth'` overrides the stylesheet's blanket
// `scroll-behavior: auto` for reduced motion, so the preference has to be read
// here rather than left to CSS.
const prefersReducedMotion = () =>
    typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// A name that is still resolving shows a shimmer bar instead of the raw user id,
// so a slow eventUsers listener never looks like broken data.
const NameOrSkeleton: React.FC<{
    name: string | null;
    label: string;
    widthClass?: string;
}> = ({ name, label, widthClass = 'w-24' }) => {
    if (name !== null) return <>{name}</>;
    return (
        <span
            role="status"
            aria-label={label}
            className={`inline-block h-4 max-w-full align-middle rounded bg-gray-200 dark:bg-gray-700 animate-pulse ${widthClass}`}
        />
    );
};

/**
 * The "split between" line: a list of names of unpredictable length.
 *
 * A phone fits two or three names on it, and plain CSS truncation then hides both
 * how many were cut off and who they were. So the head count is always on the
 * line, and the names themselves expand in place when the row is tapped — the
 * full list used to live in a detail modal, which existed largely to show it.
 *
 * Whether to offer that toggle is measured rather than guessed: the same three
 * names fit on a laptop and overflow on a phone, and the measurement is only
 * meaningful while the line is still clamped, which is why it pauses once open.
 */
const SplitBetweenNames: React.FC<{
    names: string[];
    showAllLabel: string;
    showLessLabel: string;
}> = ({ names, showAllLabel, showLessLabel }) => {
    const [expanded, setExpanded] = useState(false);
    const [overflowing, setOverflowing] = useState(false);
    const textRef = useRef<HTMLSpanElement>(null);
    const joined = names.join(', ');

    useEffect(() => {
        const el = textRef.current;
        if (!el || expanded) return;

        const measure = () => setOverflowing(el.scrollWidth > el.clientWidth + 1);
        measure();

        const observer = new ResizeObserver(measure);
        observer.observe(el);
        return () => observer.disconnect();
    }, [expanded, joined]);

    const toggle = (e: React.MouseEvent) => {
        // Nothing above listens for this today, but the row is inside a card that
        // has been clickable before and may be again.
        e.stopPropagation();
        setExpanded(value => !value);
    };

    return (
        <span
            className={`ml-2 flex-1 min-w-0 flex items-start gap-1.5 ${overflowing ? 'cursor-pointer' : ''}`}
            onClick={overflowing ? toggle : undefined}
        >
            <span
                ref={textRef}
                className={`font-medium text-gray-900 dark:text-white min-w-0 ${expanded ? 'break-words' : 'truncate'}`}
            >
                {joined}
            </span>
            {overflowing && (
                <button
                    type="button"
                    onClick={toggle}
                    aria-expanded={expanded}
                    aria-label={expanded ? showLessLabel : showAllLabel}
                    title={expanded ? showLessLabel : showAllLabel}
                    className="flex-shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors mt-0.5"
                >
                    <FontAwesomeIcon icon={expanded ? faChevronUp : faChevronDown} className="text-[10px]" />
                </button>
            )}
        </span>
    );
};

const ExpenseList: React.FC<ExpenseListProps> = ({
                                                     expenses,
                                                     eventId,
                                                     participants,
                                                     shareRatios,
                                                     currentUser,
                                                     isAdmin,
                                                     onEdit,
                                                     onDelete,
                                                     onReorder,
                                                     canEdit,
                                                     canDelete
                                                 }) => {
    const { t, i18n } = useTranslation();
    const { formatCurrency } = useFormatCurrency();

    // Get all unique user IDs from all expenses
    const allUserIds = useMemo(() => {
        const userIds = new Set<string>();
        expenses.forEach(expense => {
            if (expense.payer !== 'sponsor') { // Skip sponsor payer
                userIds.add(expense.payer);
            }
            userIds.add(expense.createdBy);
            expense.splitBetween.forEach(userId => userIds.add(userId));
        });
        return Array.from(userIds);
    }, [expenses]);

    // Live names (they reflect renames) arrive on their own listener, so they can
    // land after the expenses do. Falling straight back to the raw user id made
    // half-loaded rows read like corrupt data — `participants` covers that gap,
    // and only a genuinely unresolved name shows a placeholder.
    const { eventUsers, loading: namesLoading } = useEventUsersCache(eventId);
    const loadingNameLabel = t('components.expenseList.loadingName');

    const getUserDisplayName = (userId: string): string | null => {
        const name = eventUsers[userId]?.displayName || participants?.[userId];
        if (name) return name;
        return namesLoading ? null : userId;
    };

    // "Everyone" means every participant of the event — `allUserIds` only covers
    // the ids present in the expenses currently passed in (which filtering shrinks).
    const totalParticipantCount = participants
        ? Object.keys(participants).length
        : allUserIds.length;

    const isSponsorExpense = (expense: AnyExpense): expense is SponsorExpense => {
        return expense.type === 'sponsor';
    };

    // Default permission function if not provided
    const defaultCanEditExpense = (expense: AnyExpense): boolean => {
        // Admin can edit any expense
        if (isAdmin) return true;

        // User can edit their own expense within time limit
        if (expense.createdBy === currentUser) {
            if (expense.editableUntil && Date.now() < expense.editableUntil) {
                return true;
            }
        }

        return false;
    };

    const checkCanEdit = canEdit || defaultCanEditExpense;
    const checkCanDelete = canDelete || defaultCanEditExpense;

    const language = i18n.language || 'en';

    // Group expenses by date — must be BEFORE any early return to follow Rules of Hooks
    const groupedExpenses = useMemo(() => {
        const groups: { [dateKey: string]: { label: string; expenses: AnyExpense[] } } = {};
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);

        // Sort expenses newest first
        const sorted = [...expenses].sort((a, b) => b.timestamp - a.timestamp);

        sorted.forEach(expense => {
            const expDate = new Date(expense.timestamp);
            expDate.setHours(0, 0, 0, 0);
            const dateKey = expDate.toISOString().split('T')[0]; // YYYY-MM-DD

            if (!groups[dateKey]) {
                let label: string;
                if (expDate.getTime() === today.getTime()) {
                    label = t('components.expenseList.dateGroup.today');
                } else if (expDate.getTime() === yesterday.getTime()) {
                    label = t('components.expenseList.dateGroup.yesterday');
                } else {
                    label = new Date(expense.timestamp).toLocaleDateString(language, {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                    });
                }
                groups[dateKey] = { label, expenses: [] };
            }
            groups[dateKey].expenses.push(expense);
        });

        // Return sorted by date descending
        return Object.entries(groups)
            .sort(([a], [b]) => b.localeCompare(a))
            .map(([dateKey, group]) => ({ dateKey, ...group }));
    }, [expenses, t, language]);

    // ---- Reordering -------------------------------------------------------
    // Holding the handle on a card collapses the whole list to single lines and
    // hands the gesture over to a drag. There is no mode to enter or leave: the
    // compact layout exists only for as long as the finger is down, which is the
    // only time it is needed (a screenful of tall cards shows two of them, and you
    // cannot drag across a list you cannot see).
    //
    // While collapsed the day headings give way to one flat list, each line
    // carrying its own date. That is what lets a drag cross from one day into
    // another — and every line stays the same height, so the distance travelled
    // maps straight onto a number of places moved.

    const pressRef = useRef<PressState | null>(null);
    const dragRef = useRef<DragState | null>(null);
    // Document-space centre of the dragged row at the moment it was picked up.
    // Document rather than viewport, so edge auto-scrolling does not shift it.
    const baselineRef = useRef(0);
    const pointerYRef = useRef(0);
    const holdTimerRef = useRef<number | null>(null);
    const autoScrollRef = useRef<number | null>(null);
    // Edge scrolling stays disarmed until the finger has been clear of both zones
    // once — see runAutoScroll.
    const autoScrollArmedRef = useRef(false);
    // The list element, measured just before it collapses; see collapsedMinHeight.
    const listRef = useRef<HTMLDivElement>(null);
    const rowRefs = useRef(new Map<string, HTMLLIElement>());
    // The full-height cards and their handles, for finding an entry again after the
    // collapsed list it was dragged in has gone.
    const cardRefs = useRef(new Map<string, HTMLDivElement>());
    const handleRefs = useRef(new Map<string, HTMLButtonElement>());
    const landedTimerRef = useRef<number | null>(null);
    const onReorderRef = useRef(onReorder);
    onReorderRef.current = onReorder;

    const [pressing, setPressing] = useState(false);
    const [collapsed, setCollapsed] = useState(false);
    const [drag, setDrag] = useState<DragState | null>(null);
    const [landed, setLanded] = useState<LandedState | null>(null);
    // The height the list had as cards, held for as long as it is a row of lines.
    //
    // Collapsing a screenful of cards into 48px lines can take a couple of thousand
    // pixels out of the page, and the browser then clamps the scroll position to
    // the shorter document. The pickup scroll below — the one that puts the grabbed
    // row back under the finger — has nowhere left to go, and whatever it cannot
    // absorb becomes a permanent offset between where the row is drawn and the slot
    // it actually occupies: the row rides under the finger while the drop caret
    // tracks somewhere else entirely. Keeping the page the height it already was
    // leaves the scroll the room it needs, and the two stay together.
    const [collapsedMinHeight, setCollapsedMinHeight] = useState<number | null>(null);

    const clearHoldTimer = useCallback(() => {
        if (holdTimerRef.current !== null) {
            window.clearTimeout(holdTimerRef.current);
            holdTimerRef.current = null;
        }
    }, []);

    const stopAutoScroll = useCallback(() => {
        if (autoScrollRef.current !== null) {
            cancelAnimationFrame(autoScrollRef.current);
            autoScrollRef.current = null;
        }
    }, []);

    const updateDrag = useCallback((clientY: number) => {
        const current = dragRef.current;
        const press = pressRef.current;
        if (!current || !press) return;

        const offsetY = clientY + window.scrollY - baselineRef.current;
        const currentIndex = clamp(
            current.fromIndex + Math.round(offsetY / current.pitch),
            0,
            press.order.length - 1
        );

        if (offsetY === current.offsetY && currentIndex === current.currentIndex) return;

        const next = { ...current, offsetY, currentIndex };
        dragRef.current = next;
        setDrag(next);
    }, []);

    // Dragging to a spot that is off screen has to bring the rest of the day into
    // reach, and the row has to keep travelling while the finger sits still.
    const runAutoScroll = useCallback(() => {
        const y = pointerYRef.current;
        const topEdge = HEADER_INSET_PX + EDGE_SCROLL_ZONE;
        const bottomEdge = window.innerHeight - EDGE_SCROLL_ZONE;

        // A drag almost always begins inside the bottom zone — the handle sits in
        // the card's action row, and a card tall enough to be worth dragging puts
        // that row near the bottom of the screen. Starting at full scroll speed
        // there means the list bolts the instant it is picked up, so the zones only
        // arm once the finger has been clear of both of them.
        if (!autoScrollArmedRef.current) {
            if (y > topEdge && y < bottomEdge) autoScrollArmedRef.current = true;
        } else if (y < topEdge) {
            window.scrollBy(0, -EDGE_SCROLL_MAX_STEP * clamp((topEdge - y) / EDGE_SCROLL_ZONE, 0, 1));
            updateDrag(y);
        } else if (y > bottomEdge) {
            window.scrollBy(0, EDGE_SCROLL_MAX_STEP * clamp((y - bottomEdge) / EDGE_SCROLL_ZONE, 0, 1));
            updateDrag(y);
        }
        autoScrollRef.current = requestAnimationFrame(runAutoScroll);
    }, [updateDrag]);

    const activate = useCallback(() => {
        const press = pressRef.current;
        if (!press || press.activated) return;
        press.activated = true;
        clearHoldTimer();
        setCollapsedMinHeight(listRef.current?.getBoundingClientRect().height ?? null);
        setCollapsed(true);
    }, [clearHoldTimer]);

    // The collapse has to be on screen before the row can be measured, so the drag
    // itself only starts on the layout pass that follows it.
    useLayoutEffect(() => {
        if (!collapsed) return;
        const press = pressRef.current;
        if (!press || dragRef.current) return;

        const element = rowRefs.current.get(press.id);
        const self = element?.getBoundingClientRect();
        if (!element || !self) return;

        const first = rowRefs.current.get(press.order[0].id)?.getBoundingClientRect();
        const second = press.order[1]
            ? rowRefs.current.get(press.order[1].id)?.getBoundingClientRect()
            : undefined;
        const pitch = first && second ? second.top - first.top : self.height + COLLAPSED_GAP_PX;
        if (pitch <= 0) return;

        // The collapse reflowed the page under a finger that has not moved. Scroll
        // the grabbed row back to it, so the gesture continues from where it
        // started instead of from wherever the new layout happened to land.
        window.scrollBy(0, (self.top + self.height / 2) - pointerYRef.current);

        // Whatever the scroll could not absorb (the page was already at an end)
        // becomes a purely visual offset.
        const settled = element.getBoundingClientRect();
        const liftCorrection = pointerYRef.current - (settled.top + settled.height / 2);

        // The drag measures from the finger, so it opens at zero: the row is held,
        // not yet moved, whatever the reflow did.
        baselineRef.current = pointerYRef.current + window.scrollY;

        const fromIndex = press.order.findIndex(item => item.id === press.id);
        const next: DragState = {
            id: press.id,
            fromIndex,
            currentIndex: fromIndex,
            offsetY: 0,
            liftCorrection,
            pitch,
            rowHeight: self.height,
        };
        dragRef.current = next;
        setDrag(next);

        stopAutoScroll();
        autoScrollRef.current = requestAnimationFrame(runAutoScroll);
    }, [collapsed, runAutoScroll, stopAutoScroll]);

    const endPress = useCallback(() => {
        clearHoldTimer();
        stopAutoScroll();

        const press = pressRef.current;
        const current = dragRef.current;
        pressRef.current = null;
        dragRef.current = null;
        baselineRef.current = 0;

        setPressing(false);
        setCollapsed(false);
        setCollapsedMinHeight(null);
        setDrag(null);

        if (!press || !current) return;

        // Marked whether or not the order changed: a drag that thinks better of
        // itself still ends with the page scrolled somewhere the entry may not be,
        // and finding it again is the point — not that it moved.
        setLanded({ id: current.id, focusHandle: false });

        if (!onReorderRef.current) return;
        if (current.currentIndex === current.fromIndex) return;

        const next = [...press.order];
        const [moved] = next.splice(current.fromIndex, 1);
        next.splice(current.currentIndex, 0, moved);

        onReorderRef.current({
            order: next,
            movedId: moved.id,
            targetDayStart: targetDayFor(next, current.currentIndex, moved),
        });
    }, [clearHoldTimer, stopAutoScroll]);

    // The drop. The list springs back to full-height cards under a page still
    // scrolled where the collapsed lines left it, so the entry that was just moved
    // is usually off screen — and even when it is not, one card in a column of
    // identical cards says nothing about having been the one carried there. The
    // gesture ends by putting it back under the eye, marked.
    useEffect(() => {
        if (!landed || collapsed) return;

        let frame = 0;
        let waited = 0;

        const settle = () => {
            if (waited++ < LANDED_SETTLE_FRAMES) {
                frame = requestAnimationFrame(settle);
                return;
            }

            // Arrow keys move an entry a step at a time, and a step that crosses
            // into another day remounts the card — with it the handle, and the
            // focus that was on it, so the next press would go nowhere.
            if (landed.focusHandle) {
                handleRefs.current.get(landed.id)?.focus({ preventScroll: true });
            }

            const card = cardRefs.current.get(landed.id);
            if (!card) return;

            const rect = card.getBoundingClientRect();
            const top = HEADER_INSET_PX;
            const bottom = window.innerHeight - LANDED_VIEW_MARGIN_PX;
            // Already somewhere it can be read: moving the page under a finger that
            // has just stopped would undo the point of the exercise.
            if (rect.top >= top && rect.bottom <= bottom) return;

            // Centred in what is left of the screen, or — for a card too tall for
            // that to mean anything — head first, clear of the sticky header.
            const fits = rect.height <= bottom - top;
            const target = fits
                ? rect.top + window.scrollY - top - (bottom - top - rect.height) / 2
                : rect.top + window.scrollY - top - LANDED_VIEW_MARGIN_PX;

            window.scrollTo({
                top: Math.max(target, 0),
                behavior: prefersReducedMotion() ? 'auto' : 'smooth',
            });
        };

        frame = requestAnimationFrame(settle);
        landedTimerRef.current = window.setTimeout(() => setLanded(null), LANDED_HIGHLIGHT_MS);

        return () => {
            cancelAnimationFrame(frame);
            if (landedTimerRef.current !== null) {
                window.clearTimeout(landedTimerRef.current);
                landedTimerRef.current = null;
            }
        };
    }, [landed, collapsed]);

    // The handle that started the gesture is unmounted the moment the list
    // collapses, so pointer capture on it would take the rest of the drag with it.
    // The window keeps delivering either way.
    useEffect(() => {
        if (!pressing) return;

        const handleMove = (event: PointerEvent) => {
            const press = pressRef.current;
            if (!press) return;
            pointerYRef.current = event.clientY;

            if (!press.activated) {
                if (Math.abs(event.clientY - press.startY) > MOVE_ACTIVATE_PX) activate();
                return;
            }
            event.preventDefault();
            updateDrag(event.clientY);
        };

        window.addEventListener('pointermove', handleMove, { passive: false });
        window.addEventListener('pointerup', endPress);
        window.addEventListener('pointercancel', endPress);
        return () => {
            window.removeEventListener('pointermove', handleMove);
            window.removeEventListener('pointerup', endPress);
            window.removeEventListener('pointercancel', endPress);
        };
    }, [pressing, activate, updateDrag, endPress]);

    // The fixed bottom action bar sits over the last inch of the page, which is
    // exactly where the end of a drag needs to land. It stands down for the
    // duration; the page is told rather than asked, because the bar belongs to a
    // screen three components up.
    useEffect(() => {
        if (!collapsed) return;
        document.body.classList.add('is-reordering');
        return () => document.body.classList.remove('is-reordering');
    }, [collapsed]);

    useEffect(() => () => {
        clearHoldTimer();
        stopAutoScroll();
        if (landedTimerRef.current !== null) window.clearTimeout(landedTimerRef.current);
        document.body.classList.remove('is-reordering');
    }, [clearHoldTimer, stopAutoScroll]);

    const beginPress = (
        event: React.PointerEvent<HTMLButtonElement>,
        expense: AnyExpense,
        order: AnyExpense[]
    ) => {
        if (!onReorder || order.length < 2) return;
        event.preventDefault();
        event.stopPropagation();

        pressRef.current = {
            id: expense.id,
            order,
            startY: event.clientY,
            activated: false,
        };
        pointerYRef.current = event.clientY;
        autoScrollArmedRef.current = false;
        // Whatever the last move marked, this one is about to replace.
        setLanded(null);
        setPressing(true);

        clearHoldTimer();
        holdTimerRef.current = window.setTimeout(activate, HOLD_MS);
    };

    // Dragging is unreachable without a pointer, so the same handle moves the row
    // a step at a time from the keyboard.
    const handleHandleKeyDown = (
        event: React.KeyboardEvent<HTMLButtonElement>,
        expense: AnyExpense,
        order: AnyExpense[]
    ) => {
        if (!onReorder) return;
        if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;
        event.preventDefault();

        const fromIndex = order.findIndex(item => item.id === expense.id);
        const targetIndex = event.key === 'ArrowUp' ? fromIndex - 1 : fromIndex + 1;
        if (fromIndex === -1 || targetIndex < 0 || targetIndex >= order.length) return;

        const next = [...order];
        const [moved] = next.splice(fromIndex, 1);
        next.splice(targetIndex, 0, moved);

        setLanded({ id: moved.id, focusHandle: true });
        onReorder({
            order: next,
            movedId: moved.id,
            targetDayStart: targetDayFor(next, targetIndex, moved),
        });
    };

    // The list a drag runs over: the day headings flattened away, newest first.
    // No optimistic copy is kept — Firebase applies a write locally before it goes
    // to the server, so the reordered list is already on screen when the finger
    // lifts.
    const flatExpenses = useMemo(
        () => groupedExpenses.flatMap(group => group.expenses),
        [groupedExpenses]
    );

    // The headings the cards show, reachable by day rather than by group, so a
    // collapsed line can name the day it has been dragged onto.
    const dayLabels = useMemo(() => {
        const labels = new Map<number, string>();
        groupedExpenses.forEach(group => {
            const first = group.expenses[0];
            if (first) labels.set(startOfDay(first.timestamp), group.label);
        });
        return labels;
    }, [groupedExpenses]);

    // The list as it would stand if the finger lifted right now: every slot paired
    // with the day it would belong to, the dragged one carrying the day it is being
    // dropped onto rather than the day it came from.
    //
    // One projection answers all three questions a drag raises — what date the line
    // now reads, where the days divide, and which slot it is about to land in — and
    // it is built with the same `targetDayFor` the drop itself uses, so what is
    // shown is what will be written.
    const dragProjection = useMemo(() => {
        if (!drag) return null;
        const next = [...flatExpenses];
        if (drag.fromIndex < 0 || drag.fromIndex >= next.length) return null;

        const [moved] = next.splice(drag.fromIndex, 1);
        next.splice(drag.currentIndex, 0, moved);

        const targetDay = targetDayFor(next, drag.currentIndex, moved);
        const slotDays = next.map((expense, index) => (
            index === drag.currentIndex ? targetDay : startOfDay(expense.timestamp)
        ));
        return { slotDays, targetDay };
    }, [drag, flatExpenses]);

    if (expenses.length === 0) {
        return (
            <div className="text-center py-12">
                <FontAwesomeIcon
                    icon={faExclamationTriangle}
                    className="text-4xl text-gray-400 dark:text-gray-600 mb-4"
                />
                <p className="text-gray-500 dark:text-gray-400">
                    {t('components.expenseList.noExpenses')}
                </p>
            </div>
        );
    }

    // The drag view: one flat list, each line dated. Rendered instead of the cards
    // rather than alongside them, so a drag can travel from one day into another
    // without stepping over a heading.
    //
    // The days are still marked, but by rules drawn *over* the list instead of
    // headings sitting *in* it: the distance the finger has travelled is read as a
    // number of slots, which only holds while every slot is the same height. The
    // drop caret is drawn the same way and for the same reason.
    if (collapsed) {
        // The drag itself only opens on the layout pass *after* the collapse, so
        // this falls back to the untouched order for that one frame. It has to
        // render the same either way: the effect measures the collapsed list to
        // put the grabbed row back under the finger, and a heading that appeared
        // afterwards would push the whole list out from under it.
        const slotDays = dragProjection?.slotDays
            ?? flatExpenses.map(expense => startOfDay(expense.timestamp));
        // Measured rather than assumed: the pitch already comes off the real
        // layout, so the gap the overlays sit in should too.
        const gap = drag ? Math.max(drag.pitch - drag.rowHeight, 0) : COLLAPSED_GAP_PX;
        // The seam at the top of a slot, in the coordinates the list is drawn in.
        const seamAt = (slot: number) => (drag ? slot * drag.pitch - gap / 2 : 0);
        // Every slot whose day differs from the one above it. Slot 0 has no seam
        // above it, so its day is named over the list instead.
        const dayRules = slotDays
            .map((dayStart, slot) => ({ dayStart, slot }))
            .filter(({ dayStart, slot }) => slot > 0 && dayStart !== slotDays[slot - 1]);

        return (
            <div
                ref={listRef}
                className="select-none"
                style={collapsedMinHeight !== null ? { minHeight: `${collapsedMinHeight}px` } : undefined}
            >
                {/* The top day would otherwise be the one block of lines with no
                    rule of its own to name it. */}
                {slotDays.length > 0 && (
                    <div className="flex items-center gap-2 mb-2 px-1">
                        <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 truncate max-w-[65%]">
                            {dayLabels.get(slotDays[0]) ?? ''}
                        </span>
                        <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
                    </div>
                )}

                <div className="relative">
                    <ul className="flex flex-col list-none" style={{ gap: `${COLLAPSED_GAP_PX}px` }}>
                        {flatExpenses.map((expense, index) => {
                            const isSponsorship = isSponsorExpense(expense);
                            const isLifted = drag?.fromIndex === index;

                            // The line reads the day it is being dropped onto, not
                            // the day it was recorded on: crossing a rule rewrites
                            // the date, and that has to be visible before the finger
                            // lifts rather than discovered afterwards.
                            const ownDay = startOfDay(expense.timestamp);
                            const shownDay = isLifted && dragProjection
                                ? dragProjection.targetDay
                                : ownDay;
                            const dayChanged = shownDay !== ownDay;

                            // Lines between where the row started and where it now sits
                            // step aside by exactly one slot, so the gap follows the finger.
                            let shift = 0;
                            if (drag) {
                                if (isLifted) {
                                    shift = drag.offsetY + drag.liftCorrection;
                                } else if (
                                    drag.fromIndex < drag.currentIndex &&
                                    index > drag.fromIndex &&
                                    index <= drag.currentIndex
                                ) {
                                    shift = -drag.pitch;
                                } else if (
                                    drag.fromIndex > drag.currentIndex &&
                                    index >= drag.currentIndex &&
                                    index < drag.fromIndex
                                ) {
                                    shift = drag.pitch;
                                }
                            }

                            return (
                                <li
                                    key={expense.id}
                                    ref={(el) => {
                                        if (el) rowRefs.current.set(expense.id, el);
                                        else rowRefs.current.delete(expense.id);
                                    }}
                                    style={{
                                        // The scale rides along in the same declaration: a
                                        // Tailwind scale utility would be overwritten by this one.
                                        transform: `translateY(${shift}px) scale(${isLifted ? 1.03 : 1})`,
                                        // The lifted row tracks the finger with no easing;
                                        // the others glide.
                                        transition: isLifted ? 'none' : 'transform 160ms cubic-bezier(0.2, 0, 0, 1)',
                                        zIndex: isLifted ? 30 : undefined,
                                    }}
                                    className={`relative glass-sub-panel rounded-lg h-12 shrink-0 flex items-center gap-2 px-2 ${
                                        isLifted ? 'shadow-glass-lg ring-2 ring-blue-400/70' : ''
                                    } ${isSponsorship ? 'border border-green-200/30 dark:border-green-800/30' : ''}`}
                                >
                                    <span className="w-7 flex-shrink-0 flex items-center justify-center text-gray-400 dark:text-gray-500">
                                        <FontAwesomeIcon icon={faGripVertical} className="text-sm" />
                                    </span>

                                    {/* The date travels with the line, and turns to
                                        the accent the moment a drag has changed it */}
                                    <span className={`w-14 flex-shrink-0 text-[11px] tabular-nums ${
                                        dayChanged
                                            ? 'font-bold text-blue-600 dark:text-blue-400'
                                            : 'font-medium text-gray-500 dark:text-gray-400'
                                    }`}>
                                        {new Date(shownDay).toLocaleDateString(language, {
                                            day: 'numeric',
                                            month: 'short',
                                        })}
                                    </span>

                                    {isSponsorship && (
                                        <FontAwesomeIcon
                                            icon={faGift}
                                            className="text-green-600 dark:text-green-400 text-xs flex-shrink-0"
                                            aria-hidden="true"
                                        />
                                    )}

                                    <span className="flex-1 min-w-0 truncate text-sm font-medium text-gray-900 dark:text-white">
                                        {isSponsorship ? (expense as SponsorExpense).sponsorName : expense.description}
                                    </span>

                                    <span className={`text-sm font-bold flex-shrink-0 ${
                                        isSponsorship
                                            ? 'text-green-600 dark:text-green-400'
                                            : 'text-blue-600 dark:text-blue-400'
                                    }`}>
                                        {isSponsorship ? '+' : ''}{formatCurrency(Math.abs(expense.amount))}
                                    </span>
                                </li>
                            );
                        })}
                    </ul>

                    {/* Where one day gives way to the next. Keyed by the day itself,
                        so a rule slides to its new seam rather than being torn down
                        and rebuilt when the drag pushes a day's boundary along. */}
                    {drag && dayRules.map(({ dayStart, slot }) => (
                        <div
                            key={dayStart}
                            aria-hidden="true"
                            className="absolute inset-x-0 top-0 z-20 flex items-center gap-2 pointer-events-none"
                            style={{
                                transform: `translateY(${seamAt(slot)}px) translateY(-50%)`,
                                transition: 'transform 160ms cubic-bezier(0.2, 0, 0, 1)',
                            }}
                        >
                            <span className="px-1.5 py-px rounded-full text-[9px] font-semibold uppercase tracking-wide leading-none truncate max-w-[60%] bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-200">
                                {dayLabels.get(dayStart) ?? ''}
                            </span>
                            <div className="flex-1 h-px bg-gray-300 dark:bg-gray-600" />
                        </div>
                    ))}

                    {/* The drop caret: the seam the line will settle against. The
                        rows stepping aside already open the slot, but the slot is
                        wherever the finger last crossed a half-pitch, not where the
                        finger is — the caret is what says which of the two the drop
                        will follow. It sits under the lifted row, which keeps the
                        thing being moved the top-most thing on screen. */}
                    {drag && (
                        <div
                            aria-hidden="true"
                            className="absolute inset-x-0 top-0 z-20 flex items-center gap-1 pointer-events-none"
                            style={{
                                transform: `translateY(${seamAt(drag.currentIndex)}px) translateY(-50%)`,
                                transition: 'transform 160ms cubic-bezier(0.2, 0, 0, 1)',
                            }}
                        >
                            <span className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" />
                            <span className="flex-1 h-0.5 rounded-full bg-blue-500" />
                        </div>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6" ref={listRef}>
            {groupedExpenses.map(({ dateKey, label, expenses: groupExpenses }) => (
                <div key={dateKey}>
                    {/* Date Group Header */}
                    <div className="flex items-center gap-3 mb-3">
                        <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 dark:bg-gray-700/50 rounded-full">
                            <FontAwesomeIcon icon={faCalendarAlt} className="text-xs text-gray-400 dark:text-gray-500" />
                            <span className="text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide">
                                {label}
                            </span>
                        </div>
                        <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700"></div>
                        <span className="text-xs text-gray-400 dark:text-gray-500">
                            {groupExpenses.length} {groupExpenses.length === 1
                                ? t('components.expenseList.dateGroup.item')
                                : t('components.expenseList.dateGroup.items')}
                        </span>
                    </div>

                    <div className="space-y-4">
            {groupExpenses.map((expense) => {
                const canEditExpense = checkCanEdit(expense);
                const canDeleteExpense = checkCanDelete(expense);
                const splitBetweenNames = expense.splitBetween.map(userId => {
                    const name = getUserDisplayName(userId);
                    const ratio = getShareRatio(shareRatios, userId);
                    return name !== null && ratio !== DEFAULT_SHARE_RATIO ? `${name} (${ratio}%)` : name;
                });
                const splitBetweenResolved = splitBetweenNames.every((name): name is string => name !== null);
                const isSponsorship = isSponsorExpense(expense);
                const displayAmount = Math.abs(expense.amount); // Always show positive for display
                const canDragExpense = Boolean(onReorder) && canEditExpense && flatExpenses.length > 1;

                const isLanded = landed?.id === expense.id;

                return (
                    <div
                        key={expense.id}
                        ref={(el) => {
                            if (el) cardRefs.current.set(expense.id, el);
                            else cardRefs.current.delete(expense.id);
                        }}
                        // The card's separation used to be hover-only, which a touch
                        // screen never delivers: on a phone the entries were a
                        // 25%-white fill behind a 30%-white border on a pale
                        // gradient, i.e. no edge at all, and they ran together.
                        // The elevation and border now rest on the card; hover only
                        // deepens what is already there.
                        //
                        // The border utilities carry `!` because .glass-sub-panel
                        // sets `border` as plain CSS after @tailwind utilities and
                        // would otherwise win on source order.
                        className={`glass-sub-panel rounded-xl overflow-hidden shadow-glass-sm transition-shadow duration-150 hover:shadow-glass-lg ${
                            isSponsorship
                                ? '!border-green-400/60 dark:!border-green-500/40'
                                : '!border-white/60 dark:!border-white/10'
                        } ${isLanded ? 'expense-card-landed' : ''}`}
                    >
                        <div className="p-4 sm:p-6">
                            {/* Header */}
                            <div className="flex items-start justify-between mb-4">
                                <div className="flex-1">
                                    <div className="flex items-center mb-1">
                                        {isSponsorship && (
                                            <FontAwesomeIcon
                                                icon={faGift}
                                                className="text-green-600 dark:text-green-400 mr-2"
                                            />
                                        )}
                                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                            {isSponsorship ? expense.sponsorName : expense.description}
                                        </h3>
                                    </div>
                                    {isSponsorship && (
                                        <div className="text-sm text-green-700 dark:text-green-300 font-medium mb-1">
                                            {t('components.expenseList.sponsor').toUpperCase()}
                                        </div>
                                    )}
                                    <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                                        <FontAwesomeIcon icon={faCalendarAlt} className="mr-1" />
                                        {formatDate(expense.timestamp)}
                                    </div>
                                </div>
                                <div className="text-right flex-shrink-0">
                                    <div className={`text-xl sm:text-2xl font-bold ${
                                        isSponsorship
                                            ? 'text-green-600 dark:text-green-400'
                                            : 'text-blue-600 dark:text-blue-400'
                                    }`}>
                                        {isSponsorship ? '+' : ''}{formatCurrency(displayAmount)}
                                    </div>
                                    {/* The split, which is the number the entry was
                                        actually made for. It used to live one tap
                                        away in a detail modal that showed nothing
                                        else this card does not. */}
                                    {expense.splitBetween.length > 1 && (
                                        <div className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5 whitespace-nowrap">
                                            {formatShareRange(displayAmount, expense.splitBetween, shareRatios, formatCurrency)}{' '}
                                            {t('components.expenseList.eachPerson')}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Details */}
                            <div className="grid grid-cols-1 gap-3 sm:gap-4 mb-4">
                                {!isSponsorship && (
                                    <div className="flex items-center">
                                        <FontAwesomeIcon
                                            icon={faUser}
                                            className="text-green-600 dark:text-green-400 mr-2"
                                        />
                                        <span className="text-sm text-gray-600 dark:text-gray-400">{t('components.expenseList.paidBy')}:</span>
                                        <span className="ml-2 font-medium text-gray-900 dark:text-white">
                                            <NameOrSkeleton name={getUserDisplayName(expense.payer)} label={loadingNameLabel} />
                                        </span>
                                    </div>
                                )}
                                {/* A participant sponsor pays under one of two
                                    rules, and which one changes what everybody
                                    else owes — the other thing the detail modal
                                    was carrying on its own. */}
                                {isSponsorship && expense.sponsorPayer && expense.participantSponsorMode && (
                                    <div className="flex items-center">
                                        <FontAwesomeIcon
                                            icon={faInfoCircle}
                                            className="text-gray-400 dark:text-gray-500 mr-2 flex-shrink-0"
                                        />
                                        <span className="text-sm text-gray-600 dark:text-gray-400">
                                            {t('event.components.editSponsorForm.participantSponsorModeLabel')}:
                                        </span>
                                        <span className="ml-2 font-medium text-gray-900 dark:text-white">
                                            {expense.participantSponsorMode === 'payShare'
                                                ? t('event.components.editSponsorForm.payShareMode')
                                                : t('event.components.editSponsorForm.coverFirstMode')}
                                        </span>
                                    </div>
                                )}

                                {/* ✅ Hiển thị sponsor holder */}
                                {isSponsorship && expense.sponsorHolder && (
                                    <div className="flex items-center">
                                        <FontAwesomeIcon
                                            icon={faUser}
                                            className="text-amber-600 dark:text-amber-400 mr-2"
                                        />
                                        <span className="text-sm text-gray-600 dark:text-gray-400">
                                            {t('event.sponsorLabels.holder')}
                                        </span>
                                        <span className="ml-2 font-medium text-gray-900 dark:text-white">
                                            <NameOrSkeleton name={getUserDisplayName(expense.sponsorHolder)} label={loadingNameLabel} />
                                        </span>
                                    </div>
                                )}
                                <div className="flex items-start">
                                    <FontAwesomeIcon
                                        icon={faUsers}
                                        className={`mr-2 mt-1 flex-shrink-0 ${
                                            isSponsorship
                                                ? 'text-green-600 dark:text-green-400'
                                                : 'text-blue-600 dark:text-blue-400'
                                        }`}
                                    />
                                    <span className="text-sm text-gray-600 dark:text-gray-400 flex-shrink-0 whitespace-nowrap">
                                        {isSponsorship ? t('event.sponsorLabels.benefit') : t('components.expenseList.splitBetween')}
                                        {/* The head count stays on the line whether or
                                            not the names themselves fit on it */}
                                        {splitBetweenResolved && (
                                            <span className="ml-1.5 px-1.5 py-px rounded-full text-[10px] font-semibold align-middle bg-gray-200/70 text-gray-600 dark:bg-gray-700/70 dark:text-gray-300">
                                                {splitBetweenNames.length}
                                            </span>
                                        )}
                                        <span className="text-gray-400 dark:text-gray-500">:</span>
                                    </span>
                                    {!splitBetweenResolved ? (
                                        <span className="ml-2 font-medium text-gray-900 dark:text-white truncate">
                                            <NameOrSkeleton name={null} label={loadingNameLabel} widthClass="w-40" />
                                        </span>
                                    ) : splitBetweenNames.length === totalParticipantCount ? (
                                        <span className="ml-2 font-medium text-gray-900 dark:text-white truncate">
                                            {t('shared.participants.all')}
                                        </span>
                                    ) : (
                                        <SplitBetweenNames
                                            names={splitBetweenNames as string[]}
                                            showAllLabel={t('components.expenseList.showAll')}
                                            showLessLabel={t('components.expenseList.showLess')}
                                        />
                                    )}
                                </div>
                            </div>

                            {/* Actions */}
                            {(onEdit || onDelete || canDragExpense) && (
                                <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2 min-w-0">
                                            {/* The handle sits at the far end of the row from
                                                Edit and Delete. Next to them it was picked up
                                                by thumbs aiming for Edit, and a mis-grab here
                                                does not just miss — it re-dates the entry. */}
                                            {canDragExpense && (
                                                <>
                                                    <button
                                                        type="button"
                                                        ref={(el) => {
                                                            if (el) handleRefs.current.set(expense.id, el);
                                                            else handleRefs.current.delete(expense.id);
                                                        }}
                                                        onPointerDown={(e) => beginPress(e, expense, flatExpenses)}
                                                        onKeyDown={(e) => handleHandleKeyDown(e, expense, flatExpenses)}
                                                        style={{ touchAction: 'none' }}
                                                        title={t('components.expenseList.dragHandle')}
                                                        aria-label={t('components.expenseList.dragHandle')}
                                                        className="-ml-2 flex-shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-lg text-gray-400 dark:text-gray-500 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 cursor-grab active:cursor-grabbing transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                    >
                                                        <FontAwesomeIcon icon={faGripVertical} className="text-sm" />
                                                    </button>
                                                    <div className="w-px h-5 flex-shrink-0 bg-gray-200 dark:bg-gray-700"></div>
                                                </>
                                            )}
                                            {/* The "created by" label is dead weight on a
                                                phone — the name alone reads the same */}
                                            <div className="text-sm text-gray-500 dark:text-gray-400 truncate">
                                                <span className="hidden sm:inline">
                                                    {t('components.expenseList.createdBy')}{' '}
                                                </span>
                                                <NameOrSkeleton name={getUserDisplayName(expense.createdBy)} label={loadingNameLabel} widthClass="w-20" />
                                            </div>
                                        </div>
                                        <div className="flex items-center space-x-1 flex-shrink-0">
                                            {onEdit && canEditExpense && (
                                                <button
                                                    onClick={() => onEdit(expense)}
                                                    title={t('components.expenseList.edit')}
                                                    aria-label={t('components.expenseList.edit')}
                                                    className="inline-flex items-center justify-center w-9 h-9 sm:w-auto sm:h-auto sm:px-3 sm:py-1.5 text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                                                >
                                                    <FontAwesomeIcon icon={faEdit} className="sm:mr-1" />
                                                    <span className="hidden sm:inline">{t('components.expenseList.edit')}</span>
                                                </button>
                                            )}
                                            {onDelete && canDeleteExpense && (
                                                <button
                                                    onClick={() => onDelete(expense.id)}
                                                    title={t('components.expenseList.delete')}
                                                    aria-label={t('components.expenseList.delete')}
                                                    className="inline-flex items-center justify-center w-9 h-9 sm:w-auto sm:h-auto sm:px-3 sm:py-1.5 text-sm font-medium text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                                                >
                                                    <FontAwesomeIcon icon={faTrash} className="sm:mr-1" />
                                                    <span className="hidden sm:inline">{t('components.expenseList.delete')}</span>
                                                </button>
                                            )}
                                            {(!canEditExpense && !canDeleteExpense) && (
                                                <span className="text-xs text-gray-400 dark:text-gray-500">
                                                    {t('components.expenseList.cannotEdit')}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                );
            })}
                    </div>
                </div>
            ))}
        </div>
    );
};

export default ExpenseList;
